import React, { createContext, useReducer, useEffect } from 'react';
import { authService } from '../services/authService';

// ── Action types ─────────────────────────────────────────────────────────────
export const AUTH_ACTIONS = {
  LOGIN_START:      'LOGIN_START',
  LOGIN_SUCCESS:    'LOGIN_SUCCESS',
  LOGIN_FAILURE:    'LOGIN_FAILURE',
  LOGOUT:           'LOGOUT',
  REGISTER_START:   'REGISTER_START',
  REGISTER_SUCCESS: 'REGISTER_SUCCESS',
  REGISTER_FAILURE: 'REGISTER_FAILURE',
  RESTORE_SESSION:  'RESTORE_SESSION',
  CLEAR_ERROR:      'CLEAR_ERROR',
  UPDATE_USER:      'UPDATE_USER',
};

// ── Initial state ─────────────────────────────────────────────────────────────
export const initialState = {
  user:            null,
  token:           null,
  isAuthenticated: false,
  isLoading:       false,
  isInitializing:  true,   // true until the stored session check completes
  error:           null,
};

const COOKIE_SESSION_TOKEN = 'cookie_session';

// ── Reducer ───────────────────────────────────────────────────────────────────
export function authReducer(state, action) {
  switch (action.type) {
    case AUTH_ACTIONS.LOGIN_START:
    case AUTH_ACTIONS.REGISTER_START:
      return { ...state, isLoading: true, error: null };

    case AUTH_ACTIONS.LOGIN_SUCCESS:
      return {
        ...state,
        isLoading:       false,
        isAuthenticated: true,
        user:            action.payload.user,
        token:           action.payload.token,
        error:           null,
      };

    case AUTH_ACTIONS.REGISTER_SUCCESS:
      return { ...state, isLoading: false, error: null };

    case AUTH_ACTIONS.LOGIN_FAILURE:
    case AUTH_ACTIONS.REGISTER_FAILURE:
      return { ...state, isLoading: false, error: action.payload };

    case AUTH_ACTIONS.RESTORE_SESSION:
      return {
        ...state,
        isAuthenticated: !!(action.payload.token && action.payload.user),
        isInitializing:  false,
        user:            action.payload.user,
        token:           action.payload.token,
      };

    case AUTH_ACTIONS.LOGOUT:
      return { ...initialState };

    case AUTH_ACTIONS.CLEAR_ERROR:
      return { ...state, error: null };

    case AUTH_ACTIONS.UPDATE_USER:
      return { ...state, user: { ...state.user, ...action.payload } };

    default:
      return state;
  }
}

// ── Context ───────────────────────────────────────────────────────────────────
export const AuthContext = createContext(null);

// ── Provider ──────────────────────────────────────────────────────────────────
export function AuthProvider({ children }) {
  const [state, dispatch] = useReducer(authReducer, initialState);

  // Restore persisted session on mount
  useEffect(() => {
    let cancelled = false;

    const restoreSession = async () => {
      try {
        await authService.refreshSession();
      } catch {
        // Ignore refresh errors here; profile/local session checks below decide auth state.
      }

      const { token, user } = authService.getStoredAuth();

      if (token && user) {
        if (!cancelled) {
          dispatch({ type: AUTH_ACTIONS.RESTORE_SESSION, payload: { token, user } });
        }
        return;
      }

      try {
        const res = await authService.getProfile();
        const profileUser = res?.data?.user ?? res?.data ?? null;

        if (profileUser && !cancelled) {
          dispatch({
            type: AUTH_ACTIONS.RESTORE_SESSION,
            payload: { token: COOKIE_SESSION_TOKEN, user: profileUser },
          });
          return;
        }
      } catch {
        // No valid cookie session — fall through to unauthenticated state.
      }

      if (!cancelled) {
        dispatch({ type: AUTH_ACTIONS.RESTORE_SESSION, payload: { token: null, user: null } });
      }
    };

    restoreSession();

    return () => {
      cancelled = true;
    };
  }, []);

  const login = async (email, password) => {
    dispatch({ type: AUTH_ACTIONS.LOGIN_START });
    try {
      const res = await authService.login(email, password);
      const { token, refreshToken, user } = res.data;
      authService.storeAuth(token, refreshToken, user);
      dispatch({ type: AUTH_ACTIONS.LOGIN_SUCCESS, payload: { token, user } });
      return res;
    } catch (err) {
      dispatch({ type: AUTH_ACTIONS.LOGIN_FAILURE, payload: err.message });
      throw err;
    }
  };

  const register = async (formData, plan) => {
    dispatch({ type: AUTH_ACTIONS.REGISTER_START });
    try {
      const res  = await authService.register({ ...formData, plan });
      const token = res?.data?.token ?? res?.token ?? null;
      const user  = res?.data?.user  ?? res?.user  ?? null;
      if (token && user) {
        // Backend returned credentials — auto-login so subsequent API calls are authenticated
        authService.storeAuth(token, res?.data?.refreshToken ?? res?.refreshToken ?? '', user);
        dispatch({ type: AUTH_ACTIONS.LOGIN_SUCCESS, payload: { token, user } });
      } else {
        // No token returned — fall back to email-verify flow
        dispatch({ type: AUTH_ACTIONS.REGISTER_SUCCESS, payload: { token: null, user: null } });
      }
      return res;
    } catch (err) {
      dispatch({ type: AUTH_ACTIONS.REGISTER_FAILURE, payload: err.message });
      throw err;
    }
  };

  const logout = async () => {
    await authService.logout();
    dispatch({ type: AUTH_ACTIONS.LOGOUT });
  };

  const clearError = () => dispatch({ type: AUTH_ACTIONS.CLEAR_ERROR });

  const updateProfile = async (data) => {
    const res = await authService.updateProfile(data);
    const updated = res?.data?.user || res?.data || data;
    const merged = { ...state.user, ...updated };
    authService.storeAuth(
      state.token,
      localStorage.getItem('auth_refresh_token'),
      merged,
    );
    dispatch({ type: AUTH_ACTIONS.UPDATE_USER, payload: merged });
    return res;
  };

  return (
    <AuthContext.Provider value={{ state, login, register, logout, clearError, updateProfile }}>
      {children}
    </AuthContext.Provider>
  );
}
